import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../helper/apiCall';
import ENDPOINTS from '../../constants/endpoints';

const initialState = {
  items: [],
  selected: null,
  loading: false,
  error: null,
  pagination: { index: 0, size: 10, count: 0, pages: 0, hasPrevious: false, hasNext: false },
  bucketImages: [],
  bucketLoading: false,
  bucketError: null,
};

const appendIfPresent = (formData, key, value) => {
  if (value === undefined || value === null || value === '') return;
  formData.append(key, String(value));
};

const buildNewsCreateFormData = (payload) => {
  const formData = new FormData();
  const {
    coverImageFile,
    BranchId,
    Title,
    Content,
    PublishDate,
    IsActive,
  } = payload || {};


  appendIfPresent(formData, 'BranchId', BranchId);
  appendIfPresent(formData, 'Title', Title);
  appendIfPresent(formData, 'Content', Content);
  if (PublishDate instanceof Date) appendIfPresent(formData, 'PublishDate', PublishDate.toISOString());
  else if (PublishDate) appendIfPresent(formData, 'PublishDate', PublishDate);
  if (IsActive !== undefined && IsActive !== null) formData.append('IsActive', String(IsActive));

  if (coverImageFile) formData.append('CoverImage', coverImageFile);

  return formData;
};

export const fetchNewsList = createAsyncThunk(
  'news/fetchList',
  async ({ pageIndex, pageSize, branchId = null, isActive = null }, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      params.append('pageIndex', pageIndex);
      params.append('pageSize', pageSize);
      if (branchId) params.append('branchId', branchId);
      if (isActive !== null && isActive !== undefined) params.append('isActive', isActive);

      const response = await client.get(`${ENDPOINTS.NEWS.GET_LIST}?${params.toString()}`, {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchNewsById = createAsyncThunk(
  'news/fetchById',
  async (id, thunkAPI) => {
    try {
      const response = await client.get(ENDPOINTS.NEWS.GET_BY_ID.replace(':id', id), {
        token: thunkAPI.getState().auth.token
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createNews = createAsyncThunk(
  'news/create',
  async (body, thunkAPI) => {
    try {
      const formData = buildNewsCreateFormData(body);
      const response = await client.post(ENDPOINTS.NEWS.CREATE, null, {
        token: thunkAPI.getState().auth.token,
        customBody: formData,
        headers: {}
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateNews = createAsyncThunk(
  'news/update',
  async (body, thunkAPI) => {
    try {
      var formData = buildNewsCreateFormData(body);
      const response = await client.put(ENDPOINTS.NEWS.UPDATE.replace(':id', body.id), null, {
        token: thunkAPI.getState().auth.token,
        customBody: formData,
        headers: {}
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateNewsCoverImage = createAsyncThunk(
  'news/updateCoverImage',
  async ({ id, coverImageFile }, thunkAPI) => {
    try {
      const formData = new FormData();
      if (coverImageFile) formData.append('CoverImage', coverImageFile);
      const url = ENDPOINTS.NEWS.UPDATE_COVER_IMAGE.replace(':id', id);
      const response = await client(url, {
        method: 'PUT',
        token: thunkAPI.getState().auth.token,
        customBody: formData,
        headers: {}
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteNews = createAsyncThunk(
  'news/delete',
  async (id, thunkAPI) => {
    try {
      const response = await client.delete(ENDPOINTS.NEWS.DELETE.replace(':id', id), null, {
        token: thunkAPI.getState().auth.token
      });
      return { id, ...response.data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);


const newsSlice = createSlice({
  name: 'news',
  initialState,
  reducers: {
    clearSelected: (state) => { state.selected = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNewsList.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchNewsList.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload?.items)) {
          state.items = action.payload.items;
          state.pagination = {
            index: action.payload.index,
            size: action.payload.size,
            count: action.payload.count,
            pages: action.payload.pages,
            hasPrevious: action.payload.hasPrevious,
            hasNext: action.payload.hasNext,
          };
        } else {
          state.items = action.payload || [];
        }
      })
      .addCase(fetchNewsList.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      .addCase(fetchNewsById.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchNewsById.fulfilled, (state, action) => {
        state.loading = false; state.selected = action.payload;
      })
      .addCase(fetchNewsById.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      .addCase(createNews.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(createNews.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createNews.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      .addCase(updateNews.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(updateNews.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateNews.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

      .addCase(updateNewsCoverImage.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateNewsCoverImage.fulfilled, (state) => { state.loading = false; })
      .addCase(updateNewsCoverImage.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(deleteNews.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(deleteNews.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteNews.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })

  }
});

export const { clearSelected } = newsSlice.actions;
export default newsSlice.reducer; 